import { yupResolver } from '@hookform/resolvers/yup';
import Form from 'antd/lib/form/Form';
import { useGetAppSettingsQuery, useSaveAppSettingsMutation } from 'gql/types';
import { Alert, Spin } from 'modules/common/components';
import { UnsavedChangesModal } from 'modules/common/components/UnsavedChangesModal';
import { useUnsavedChangesWarning } from 'modules/common/hooks/useUnsavedChangesWarning';
import { GET_APP_SETTINGS_QUERY } from 'modules/settings/graphql/queries';
import { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ControlButtons from './ControlButtons';
import MicrochipSettingsSection from './MicrochipSection';
import { donorSettingsSchema, DonorSettingsSchemaType } from './schema';
import styles from './styles.module.scss';

const SETTINGS_TAB = 'donors';

const DonorSettings: FC = () => {
  const { t } = useTranslation('settings.DonorSettings');
  const donorSettingsForm = useForm<DonorSettingsSchemaType>({
    resolver: yupResolver(donorSettingsSchema),
    mode: 'onChange',
  });

  const {
    reset: resetForm,
    formState: { isValid, isDirty },
  } = donorSettingsForm;

  const getAppSettingsQuery = useGetAppSettingsQuery({
    variables: { input: { settingsTab: SETTINGS_TAB } },
  });

  const [saveAppSettings, saveAppSettingsMutation] =
    useSaveAppSettingsMutation();

  const settingsData = useMemo(() => {
    if (getAppSettingsQuery.data?.getAppSettings) {
      return getAppSettingsQuery.data.getAppSettings
        .settingsJson as DonorSettingsSchemaType;
    }
  }, [getAppSettingsQuery.data]);

  useEffect(() => {
    if (settingsData) {
      resetForm(settingsData);
    }
  }, [settingsData, resetForm]);

  const handleSave = () => {
    const values = donorSettingsForm.getValues();
    saveAppSettings({
      refetchQueries: [
        {
          query: GET_APP_SETTINGS_QUERY,
          variables: { input: { settingsTab: SETTINGS_TAB } },
        },
      ],
      variables: {
        input: {
          settingsJson: values,
          settingsTab: SETTINGS_TAB,
        },
      },
    });
  };

  const handleCancelChanges = () => {
    resetForm();
  };

  const {
    isModalOpen,
    handleStay,
    handleDiscard,
    handleSaveAndLeave,
    canSaveAndLeave,
  } = useUnsavedChangesWarning({
    isDirty,
    onSave: handleSave,
    isFormValid: isValid,
  });

  const canSave = isDirty && isValid;
  const isLoading = getAppSettingsQuery.loading;

  if (getAppSettingsQuery.error) {
    const message = `${t('fetchingError')}: ${
      getAppSettingsQuery.error.message
    }`;
    return (
      <Alert type="error" className={styles.errorAlert} message={message} />
    );
  }

  return (
    <Spin spinning={isLoading}>
      <FormProvider {...donorSettingsForm}>
        <Form layout="vertical">
          <MicrochipSettingsSection />
          <ControlButtons
            canSave={canSave}
            canCancel={isDirty}
            onSave={handleSave}
            onCancel={handleCancelChanges}
          />
        </Form>
      </FormProvider>
      <UnsavedChangesModal
        isOpen={isModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
        onSaveAndLeave={handleSaveAndLeave}
        canSaveAndLeave={canSaveAndLeave}
        isSaving={saveAppSettingsMutation.loading}
      />
    </Spin>
  );
};

export default DonorSettings;
